import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import DeleteForeverSharp from '@material-ui/icons/DeleteForeverSharp';
import CloseIcon from '@material-ui/icons/Close';
import { Link, Typography, IconButton, Tooltip } from '@material-ui/core';
import { useMeContext } from '~/modules/me';
import { AttachmentIcon } from '../../icons';
import AttachmentsListSubTitleRow from './AttachmentsListSubTitleRow';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    paddingBottom: theme.spacing(2)
  },
  rootEnhanced: {
    display: 'flex',
    alignItems: 'stretch',
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden'
  },
  info: {
    display: 'inline-block',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    padding: theme.spacing(0, 2)
  },
  infoEnhanced: {
    overflow: 'hidden',
    padding: theme.spacing(1, 1, 0.5, 1)
  },
  infoText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(0, 1),
    flexShrink: 0
  },
  actionButton: {
    marginTop: theme.spacing(-1.25)
  },
  actionButtonEnhanced: {
    marginRight: theme.spacing(-0.625),
    marginTop: theme.spacing(-1)
  },
  spacer: {
    flexGrow: 1,
    flexShrink: 1
  }
}));

export const AttachmentsListRow = ({
  attachment,
  deleteAttachment,
  editable,
  AttachmentStatusComponent
}) => {
  const classes = useStyles();
  const {
    featureFlags: { isPsaPraaResourceMatchingEnabled }
  } = useMeContext();

  const onDelete = useCallback(
    () =>
      deleteAttachment({
        uploadUri: attachment.uploadUri,
        type: attachment.type
      }),
    [attachment, deleteAttachment]
  );

  const fileInfo = useMemo(
    () => (
      <>
        <Tooltip title={attachment.fileName}>
          <Link
            href={attachment.linkUri}
            target="_blank"
            rel="noopener noreferrer"
            color={isPsaPraaResourceMatchingEnabled ? 'textPrimary' : 'primary'}
            underline={isPsaPraaResourceMatchingEnabled ? 'none' : 'hover'}
          >
            {isPsaPraaResourceMatchingEnabled ? (
              <Typography variant="body2" noWrap>
                <strong>{attachment.fileName || attachment.linkUri}</strong>
              </Typography>
            ) : (
              attachment.fileName || attachment.linkUri
            )}
          </Link>
        </Tooltip>
        <AttachmentsListSubTitleRow attachment={attachment} />
      </>
    ),
    [attachment, isPsaPraaResourceMatchingEnabled]
  );

  return (
    <div
      className={
        isPsaPraaResourceMatchingEnabled ? classes.rootEnhanced : classes.root
      }
    >
      {isPsaPraaResourceMatchingEnabled ? (
        <div className={classes.iconContainer}>
          <AttachmentIcon attachment={attachment} />
        </div>
      ) : (
        <AttachmentIcon attachment={attachment} />
      )}
      <div
        className={
          isPsaPraaResourceMatchingEnabled ? classes.infoEnhanced : classes.info
        }
      >
        {isPsaPraaResourceMatchingEnabled ? (
          <>
            <div className={classes.infoText}>{fileInfo}</div>
            {AttachmentStatusComponent && (
              <AttachmentStatusComponent attachment={attachment} />
            )}
          </>
        ) : (
          fileInfo
        )}
      </div>
      <div className={classes.spacer} />
      {editable && (
        <div
          className={
            isPsaPraaResourceMatchingEnabled
              ? classes.actionButtonEnhanced
              : classes.actionButton
          }
        >
          <IconButton
            color={isPsaPraaResourceMatchingEnabled ? 'default' : 'secondary'}
            aria-label="delete"
            onClick={onDelete}
          >
            {isPsaPraaResourceMatchingEnabled ? (
              <CloseIcon data-testid="close-icon" />
            ) : (
              <DeleteForeverSharp data-testid="delete-icon" />
            )}
          </IconButton>
        </div>
      )}
    </div>
  );
};

AttachmentsListRow.propTypes = {
  attachment: PropTypes.object.isRequired,
  deleteAttachment: PropTypes.func.isRequired,
  editable: PropTypes.bool,
  AttachmentStatusComponent: PropTypes.func
};

export default AttachmentsListRow;
